import { GlobalVariables } from "@/helpers/globalVariables"
import { useReportStore } from "@/stores/report"
import { useUserStore } from "@/stores/user"
import { createRouter, createWebHistory, type NavigationGuardNext, type RouteLocationNormalized } from "vue-router"

import CoachingStatus, { CoachingStatusValues } from "@/shared/enums/CoachingStatus"
import ReportStatus, { ReportStatusValues, ActiveSubscriptionReportStatusValues } from "@/shared/enums/ReportStatus"

import Confirmation from "@/views/Confirmation.vue"
import Error from "@/views/Error.vue"
import Login from "@/views/Login.vue"
import FirstStep from "@/views/FirstStep.vue"
import Register from "@/views/Register.vue"
import ThankYou from "@/views/ThankYou.vue"

import Magic from "@/views/auth/Magic.vue"

import JobQuiz from "@/views/jobTest/JobQuiz.vue"
import JobTest from "@/views/jobTest/JobTest.vue"
import CoachingQuiz from "@/views/jobTest/QuizCoaching.vue"
import CoachingQuestionnaire from "@/views/jobTest/QuestionnaireCoaching.vue"

import Loading from "@/views/loading/Loading.vue"
import PleaseWait from "@/views/loading/PleaseWait.vue"
import Processing from "@/views/loading/Processing.vue"

import Begin from "@/views/preview/Begin.vue"
import Discover from "@/views/preview/Discover.vue"
import GetStarted from "@/views/preview/GetStarted.vue"
import Hello from "@/views/preview/Hello.vue"
import Start from "@/views/preview/Start.vue"
import Welcome from "@/views/preview/Welcome.vue"

import Report from "@/views/report/Report.vue"
import SectionAccount from "@/views/report/sections/Account.vue"
import SectionCareers from "@/views/report/sections/Careers.vue"
import SectionCareersOverview from "@/views/report/sections/CareersOverview.vue"
import SectionConfirmation from "@/views/report/sections/ReportConfirmation.vue"
import SectionFeedback from "@/views/report/sections/Feedback.vue"
import SectionNewCareers from "@/views/report/sections/GetNewCareers.vue"
import SectionCoachingHome from "@/views/report/sections/CoachingHome.vue"
import SectionCoachingMeetingNotes from "@/views/report/sections/CoachingMeetingNotes.vue"
import SectionGroupCoaching from "@/views/report/sections/GroupCoaching.vue"
import SectionHome from "@/views/report/sections/Home.vue"
import SectionOutdated from "@/views/report/sections/Outdated.vue"
import SectionPersonality from "@/views/report/sections/Personality.vue"
import SectionCheckout from "@/views/report/sections/ReportCheckout.vue"
import SectionResources from "@/views/report/sections/Resources.vue"
import SectionResumeBuilder from "@/views/report/sections/ResumeBuilder.vue"
import SectionCoachingUpsell from "@/views/report/sections/CoachingUpsell.vue"
import SectionTestAdjustment from "@/views/report/sections/TestAdjustment.vue"
import SectionTestRetake from "@/views/report/sections/TestRetake.vue"

import Coaching from "@/views/coaching/Coaching.vue"
import CoachingConfirmation from "@/views/coaching/sections/CoachingConfirmation.vue"
import ExtraSessionConfirmation from "@/views/coaching/sections/ExtraSessionConfirmation.vue"
import CoachingLoading from "@/views/coaching/sections/CoachingLoading.vue"
import CoachingLifecycle from "@/views/coaching/sections/Lifecycle.vue"
import Cart from "@/views/coaching/sections/Cart.vue"
import ChooseCoach from "@/views/coaching/sections/ChooseCoach.vue"
import SelectCoach from "@/views/coaching/sections/SelectCoach.vue"
import ChoosePlan from "@/views/coaching/sections/ChoosePlan.vue"
import SelectPlan from "@/views/coaching/sections/SelectPlan.vue"
import CoachingCoachingBundle from "@/views/coaching/sections/CoachingBundle.vue"

import ResumeConfirmation from "@/views/resume/ResumeConfirmation.vue"

import ResumeBuilderConfirmation from "@/views/resumeBuilder/ResumeBuilderConfirmation.vue"
import GtmService from "@/services/gtmService"

declare module "vue-router" {
  interface RouteMeta {
    /**
     * Title of the route that will be displayed in the browser tab
     */
    title: string

    /**
     * If set to true, the route can be accessed by unauthenticated users
     */
    allowAnonymous?: boolean

    /**
     * List of report statuses that are blocked from accessing the route
     */
    blockedReportStatuses: ReportStatus[]

    /**
     * List of coaching statuses that are blocked from accessing the route
     */
    blockedCoachingStatuses: CoachingStatus[] // Coaching statuses exempt from this list can access the route

    /**
     * Custom function to run when the user is authorized to access the route
     */
    onAuthorized?: (to: RouteLocationNormalized, next: NavigationGuardNext) => any
  }
}

// Constants
const COACHING_TITLE = "Career Coaching - JobTest.org"
const GROUP_COACHING_TITLE = "The JobTest Accelerator - JobTest.org"
const SELECT_COACH_TITLE = "We Found Your Perfect Match! - JobTest.org"
const COACHING_LOADING_TITLE = "Finding you the perfect coach... - JobTest.org"
const DEFAULT_TITLE = "Career Test - JobTest.org"
const ERROR_TITLE = "Error - JobTest.org"
const LOADING_TITLE = "Loading Test Results - JobTest.org"
const LOGIN_TITLE = "Login - JobTest.org"
const LOGOUT_TITLE = "Logout - JobTest.org"
const REGISTER_TITLE = "Register - JobTest.org"
const PREVIEW_TITLE = "Career Test Preview - JobTest.org"
const QUIZ_TITLE = "Take the Job Test - JobTest.org"
const REPORT_TITLE = "Career Test Report - JobTest.org"
const RESUME_BUILDER_TITLE = "Job Test Ready Resume Builder - JobTest.org"
const COACHING_UPSELL_TITLE = "Meet a Personalized Coach - JobTest.org"
const THANK_YOU_TITLE = "Thank You - JobTest.org"

const EVERY_REPORT_STATUS_EXCEPT_FINISHED = ReportStatusValues.filter((status) => status !== ReportStatus.FINISHED)
const EVERY_REPORT_STATUS = ReportStatusValues
const EVERY_FULL_REPORT_STATUS = [
  ReportStatus.FREE,
  ReportStatus.BASIC,
  ReportStatus.COMPREHENSIVE,
  ReportStatus.MONTHLY,
  ReportStatus.QUARTERLY,
  ReportStatus.ANNUAL,
  ReportStatus.CANCELED,
  ReportStatus.PREMIUM
]
const EVERY_NON_GROUP_COACHING_REPORT_STATUS = ReportStatusValues.filter(
  (status) => !ActiveSubscriptionReportStatusValues.includes(status)
)
const EVERY_PREVIEW_REPORT_STATUS = [ReportStatus.FINISHED, ReportStatus.REFUND, ReportStatus.NONE]
const EVERY_UNPAID_REPORT_STATUS = [ReportStatus.FINISHED, ReportStatus.REFUND, ReportStatus.NONE, ReportStatus.FREE]
const EVERY_COACHING_STATUS = CoachingStatusValues
const EVERY_COACHING_STATUS_EXCEPT_FINISHED = CoachingStatusValues.filter(
  (status) => status !== CoachingStatus.FINISHED
)
const EVERY_UNPAID_COACHING_STATUS = [CoachingStatus.NONE, CoachingStatus.FINISHED]
const EVERY_PAID_COACHING_STATUS = CoachingStatusValues.filter(
  (status) => ![CoachingStatus.NONE, CoachingStatus.FINISHED].includes(status)
)

// Variable used to reduce code verbosity
const AUTH_SECTIONS = GlobalVariables.urls.auth.children
const COACHING_SECTIONS = GlobalVariables.urls.coaching.children
const REPORT_SECTIONS = GlobalVariables.urls.report.children
const SECTIONS = GlobalVariables.urls

// Default redirections
const REDIRECT_COACHING_HOME = COACHING_SECTIONS.home.routeName
const REDIRECT_COACHING_CHOOSE_PLAN = COACHING_SECTIONS.choosePlan.routeName
const REDIRECT_COACHING_SELECT_PLAN = COACHING_SECTIONS.selectPlan.routeName
const REDIRECT_LOGIN_PAGE = SECTIONS.login.routeName
const REDIRECT_REPORT_PAGE = REPORT_SECTIONS.home.routeName
const REDIRECT_UNAUTHENTICATED = SECTIONS.login.routeName
const REDIRECT_REPORT_PAYWALL = SECTIONS.getStarted.routeName
const REDIRECT_TEST_RETAKE = REPORT_SECTIONS.testRetake.routeName

// Definition of Vue Routes
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: SECTIONS.root.path,
      name: SECTIONS.root.routeName,
      component: Login, // Required as we are using custom redirection
      meta: {
        blockedReportStatuses: EVERY_REPORT_STATUS,
        blockedCoachingStatuses: EVERY_COACHING_STATUS,
        title: LOGIN_TITLE
      }
    },
    // TODO: Move login into auth section ?
    {
      path: SECTIONS.login.path,
      name: SECTIONS.login.routeName,
      component: Login,
      meta: {
        allowAnonymous: true,
        blockedReportStatuses: EVERY_REPORT_STATUS,
        blockedCoachingStatuses: EVERY_COACHING_STATUS,
        title: LOGIN_TITLE
      }
    },
    {
      path: SECTIONS.logout.path,
      name: SECTIONS.logout.routeName,
      component: Login, // Required as we are using custom redirection
      meta: {
        allowAnonymous: true,
        blockedReportStatuses: [],
        blockedCoachingStatuses: [],
        title: LOGOUT_TITLE,
        onAuthorized: async (_to: any, next: any) => {
          await useUserStore().logout()
          next({ name: REDIRECT_LOGIN_PAGE })
        }
      }
    },
    {
      path: SECTIONS.register.path,
      name: SECTIONS.register.routeName,
      component: Register,
      meta: {
        allowAnonymous: true,
        blockedReportStatuses: EVERY_REPORT_STATUS,
        blockedCoachingStatuses: EVERY_COACHING_STATUS,
        title: REGISTER_TITLE
      }
    },
    {
      path: SECTIONS.firstStep.path,
      name: SECTIONS.firstStep.routeName,
      component: FirstStep,
      meta: {
        allowAnonymous: true,
        blockedReportStatuses: EVERY_REPORT_STATUS,
        blockedCoachingStatuses: EVERY_COACHING_STATUS,
        title: COACHING_TITLE
      }
    },
    {
      path: SECTIONS.auth.path,
      children: [
        {
          path: SECTIONS.auth.path + AUTH_SECTIONS.magic.path,
          name: AUTH_SECTIONS.magic.routeName,
          component: Magic,
          meta: {
            allowAnonymous: true,
            blockedReportStatuses: [],
            blockedCoachingStatuses: [],
            title: DEFAULT_TITLE
          }
        }
      ]
    },
    {
      path: SECTIONS.error.path,
      name: SECTIONS.error.routeName,
      component: Error,
      meta: {
        allowAnonymous: true,
        blockedReportStatuses: [],
        blockedCoachingStatuses: [],
        title: ERROR_TITLE
      }
    },
    {
      path: SECTIONS.thankYou.path,
      name: SECTIONS.thankYou.routeName,
      component: ThankYou,
      meta: {
        allowAnonymous: true,
        blockedReportStatuses: [],
        blockedCoachingStatuses: [],
        title: THANK_YOU_TITLE
      }
    },
    // This is the the post-payment page for any payments that do not upgrade the user
    // i.e. Coaching on-demand
    {
      path: SECTIONS.confirmation.path,
      name: SECTIONS.confirmation.routeName,
      component: Confirmation,
      meta: {
        allowAnonymous: true,
        blockedReportStatuses: [],
        blockedCoachingStatuses: [],
        title: THANK_YOU_TITLE
      }
    },
    // ***
    // Quiz Routes
    // ***
    {
      path: SECTIONS.jobTest.path,
      name: SECTIONS.jobTest.routeName,
      component: JobTest,
      meta: {
        allowAnonymous: true,
        blockedReportStatuses: EVERY_REPORT_STATUS,
        blockedCoachingStatuses: EVERY_COACHING_STATUS,
        title: QUIZ_TITLE
      }
    },
    {
      path: SECTIONS.jobQuiz.path,
      name: SECTIONS.jobQuiz.routeName,
      component: JobQuiz,
      meta: {
        allowAnonymous: true,
        blockedReportStatuses: EVERY_REPORT_STATUS,
        blockedCoachingStatuses: EVERY_COACHING_STATUS,
        title: QUIZ_TITLE
      }
    },
    // ***
    // Loading Animation (Pre-Paywall) Routes
    // ***
    {
      path: SECTIONS.loading.path,
      name: SECTIONS.loading.routeName,
      component: Loading,
      meta: {
        blockedReportStatuses: EVERY_FULL_REPORT_STATUS,
        blockedCoachingStatuses: [],
        title: LOADING_TITLE
      }
    },
    {
      path: SECTIONS.pleaseWait.path,
      name: SECTIONS.pleaseWait.routeName,
      component: PleaseWait,
      meta: {
        blockedReportStatuses: EVERY_FULL_REPORT_STATUS,
        blockedCoachingStatuses: [],
        title: LOADING_TITLE
      }
    },
    {
      path: SECTIONS.processing.path,
      name: SECTIONS.processing.routeName,
      component: Processing,
      meta: {
        blockedReportStatuses: EVERY_FULL_REPORT_STATUS,
        blockedCoachingStatuses: [],
        title: LOADING_TITLE
      }
    },
    // ***
    // Preview (Paywall) Routes
    // ***
    {
      path: SECTIONS.welcome.path,
      name: SECTIONS.welcome.routeName,
      component: Welcome,
      meta: {
        blockedReportStatuses: EVERY_REPORT_STATUS_EXCEPT_FINISHED,
        blockedCoachingStatuses: [],
        title: PREVIEW_TITLE
      }
    },
    {
      path: SECTIONS.hello.path,
      name: SECTIONS.hello.routeName,
      component: Hello,
      meta: {
        blockedReportStatuses: EVERY_REPORT_STATUS_EXCEPT_FINISHED,
        blockedCoachingStatuses: [],
        title: PREVIEW_TITLE
      }
    },
    {
      path: SECTIONS.begin.path,
      name: SECTIONS.begin.routeName,
      component: Begin,
      meta: {
        blockedReportStatuses: EVERY_REPORT_STATUS_EXCEPT_FINISHED,
        blockedCoachingStatuses: [],
        title: PREVIEW_TITLE
      }
    },
    {
      path: SECTIONS.discover.path,
      name: SECTIONS.discover.routeName,
      component: Discover,
      meta: {
        blockedReportStatuses: EVERY_REPORT_STATUS_EXCEPT_FINISHED,
        blockedCoachingStatuses: [],
        title: PREVIEW_TITLE
      }
    },
    {
      path: SECTIONS.start.path,
      name: SECTIONS.start.routeName,
      component: Start,
      meta: {
        blockedReportStatuses: EVERY_REPORT_STATUS_EXCEPT_FINISHED,
        blockedCoachingStatuses: [],
        title: PREVIEW_TITLE
      }
    },
    {
      path: SECTIONS.getStarted.path,
      name: SECTIONS.getStarted.routeName,
      component: GetStarted,
      meta: {
        blockedReportStatuses: EVERY_REPORT_STATUS_EXCEPT_FINISHED,
        blockedCoachingStatuses: [],
        title: PREVIEW_TITLE
      }
    },
    // ***
    // Coaching Routes
    // ***
    {
      path: SECTIONS.coaching.path,
      name: SECTIONS.coaching.routeName,
      component: Coaching, // Required as we are using custom redirection
      meta: {
        blockedReportStatuses: EVERY_REPORT_STATUS,
        blockedCoachingStatuses: EVERY_COACHING_STATUS,
        title: COACHING_TITLE
      },
      children: [
        {
          path: SECTIONS.coaching.path + COACHING_SECTIONS.quiz.path,
          name: COACHING_SECTIONS.quiz.routeName,
          component: CoachingQuiz,
          meta: {
            allowAnonymous: true,
            blockedReportStatuses: EVERY_REPORT_STATUS,
            blockedCoachingStatuses: EVERY_COACHING_STATUS,
            title: COACHING_TITLE
          }
        },
        {
          path: SECTIONS.coaching.path + COACHING_SECTIONS.questionnaire.path,
          name: COACHING_SECTIONS.questionnaire.routeName,
          component: CoachingQuestionnaire,
          meta: {
            allowAnonymous: true,
            blockedReportStatuses: EVERY_REPORT_STATUS,
            blockedCoachingStatuses: EVERY_COACHING_STATUS,
            title: COACHING_TITLE
          }
        },
        {
          path: SECTIONS.coaching.path + COACHING_SECTIONS.lifecycle.path + "/:slug",
          name: COACHING_SECTIONS.lifecycle.routeName,
          component: CoachingLifecycle,
          meta: {
            blockedReportStatuses: [],
            blockedCoachingStatuses: [
              CoachingStatus.BIWEEKLY,
              CoachingStatus.MONTHLY,
              CoachingStatus.NONE,
              CoachingStatus.WEEKLY,
              CoachingStatus.PROFESSIONAL,
              CoachingStatus.EXECUTIVE
            ],
            title: COACHING_TITLE
          }
        },
        {
          path: SECTIONS.coaching.path + COACHING_SECTIONS.loading.path,
          name: COACHING_SECTIONS.loading.routeName,
          component: CoachingLoading,
          meta: {
            blockedReportStatuses: EVERY_FULL_REPORT_STATUS,
            blockedCoachingStatuses: EVERY_PAID_COACHING_STATUS,
            title: COACHING_LOADING_TITLE
          }
        },
        {
          path: SECTIONS.coaching.path + COACHING_SECTIONS.chooseCoach.path,
          name: COACHING_SECTIONS.chooseCoach.routeName,
          component: ChooseCoach,
          meta: {
            blockedReportStatuses: [],
            blockedCoachingStatuses: EVERY_COACHING_STATUS_EXCEPT_FINISHED,
            sectionTitle: COACHING_SECTIONS.chooseCoach.routeTitle,
            title: SELECT_COACH_TITLE
          }
        },
        {
          path: SECTIONS.coaching.path + COACHING_SECTIONS.selectCoach.path,
          name: COACHING_SECTIONS.selectCoach.routeName,
          component: SelectCoach,
          meta: {
            blockedReportStatuses: [],
            blockedCoachingStatuses: EVERY_COACHING_STATUS_EXCEPT_FINISHED,
            sectionTitle: COACHING_SECTIONS.selectCoach.routeTitle,
            title: SELECT_COACH_TITLE
          }
        },
        {
          path: SECTIONS.coaching.path + COACHING_SECTIONS.cart.path,
          name: COACHING_SECTIONS.cart.routeName,
          component: Cart,
          meta: {
            blockedReportStatuses: [],
            blockedCoachingStatuses: EVERY_COACHING_STATUS_EXCEPT_FINISHED,
            sectionTitle: COACHING_SECTIONS.cart.routeTitle,
            title: COACHING_TITLE
          }
        },
        {
          path: SECTIONS.coaching.path + COACHING_SECTIONS.selectTime.path,
          redirect: SECTIONS.coaching.path + COACHING_SECTIONS.selectCoach.path
        },
        {
          path: SECTIONS.coaching.path + COACHING_SECTIONS.choosePlan.path,
          name: COACHING_SECTIONS.choosePlan.routeName,
          component: ChoosePlan,
          meta: {
            blockedReportStatuses: [],
            blockedCoachingStatuses: [CoachingStatus.NONE],
            sectionTitle: COACHING_SECTIONS.choosePlan.routeTitle,
            title: COACHING_TITLE
          }
        },
        {
          path: SECTIONS.coaching.path + COACHING_SECTIONS.selectPlan.path,
          name: COACHING_SECTIONS.selectPlan.routeName,
          component: SelectPlan,
          meta: {
            blockedReportStatuses: [],
            blockedCoachingStatuses: [CoachingStatus.NONE],
            sectionTitle: COACHING_SECTIONS.selectPlan.routeTitle,
            title: COACHING_TITLE
          }
        },
        {
          path: SECTIONS.coaching.path + COACHING_SECTIONS.confirmation.path,
          name: COACHING_SECTIONS.confirmation.routeName,
          component: CoachingConfirmation,
          meta: {
            allowAnonymous: true,
            blockedReportStatuses: [],
            blockedCoachingStatuses: [],
            sectionTitle: COACHING_SECTIONS.confirmation.routeTitle,
            title: COACHING_TITLE
          }
        },
        {
          path: SECTIONS.coaching.path + COACHING_SECTIONS.confirmed.path,
          name: COACHING_SECTIONS.confirmed.routeName,
          component: CoachingConfirmation,
          meta: {
            allowAnonymous: true,
            blockedReportStatuses: [],
            blockedCoachingStatuses: [],
            sectionTitle: COACHING_SECTIONS.confirmed.routeTitle,
            title: COACHING_TITLE
          }
        },
        {
          path: SECTIONS.coaching.path + COACHING_SECTIONS.thankYou.path,
          name: COACHING_SECTIONS.thankYou.routeName,
          component: ExtraSessionConfirmation,
          meta: {
            allowAnonymous: true,
            blockedReportStatuses: [],
            blockedCoachingStatuses: [],
            sectionTitle: COACHING_SECTIONS.thankYou.routeTitle,
            title: COACHING_TITLE
          }
        }
      ]
    },
    // ***
    // Resume Routes
    // ***
    {
      path: SECTIONS.resume.path + SECTIONS.resume.children.confirmation.path,
      name: SECTIONS.resume.children.confirmation.routeName,
      component: ResumeConfirmation,
      meta: {
        allowAnonymous: true,
        blockedReportStatuses: [],
        blockedCoachingStatuses: [],
        sectionTitle: SECTIONS.resume.children.confirmation.routeTitle,
        title: THANK_YOU_TITLE
      }
    },
    // ***
    // Resume Builder Routes
    // ***
    {
      path: SECTIONS.resumeBuilder.path + SECTIONS.resumeBuilder.children.confirmation.path,
      name: SECTIONS.resumeBuilder.children.confirmation.routeName,
      component: ResumeBuilderConfirmation,
      meta: {
        allowAnonymous: true,
        blockedReportStatuses: [],
        blockedCoachingStatuses: [],
        sectionTitle: SECTIONS.resumeBuilder.children.confirmation.routeTitle,
        title: THANK_YOU_TITLE
      }
    },
    // ***
    // Report Routes
    // ***
    {
      path: SECTIONS.report.path,
      name: SECTIONS.report.routeName,
      component: Report,
      meta: {
        blockedReportStatuses: EVERY_REPORT_STATUS,
        blockedCoachingStatuses: EVERY_COACHING_STATUS,
        title: REPORT_TITLE
      },
      children: [
        {
          path: SECTIONS.report.path + REPORT_SECTIONS.home.path,
          name: REPORT_SECTIONS.home.routeName,
          component: SectionHome,
          meta: {
            blockedReportStatuses: EVERY_PREVIEW_REPORT_STATUS,
            blockedCoachingStatuses: [],
            onAuthorized: onAuthorizedReport,
            sectionTitle: null,
            title: REPORT_TITLE
          }
        },
        {
          path: SECTIONS.report.path + REPORT_SECTIONS.personality.path,
          name: REPORT_SECTIONS.personality.routeName,
          component: SectionPersonality,
          meta: {
            blockedReportStatuses: EVERY_PREVIEW_REPORT_STATUS,
            blockedCoachingStatuses: [],
            onAuthorized: onAuthorizedReport,
            sectionTitle: null,
            title: REPORT_TITLE,
            isAnchored: true
          }
        },
        {
          path: SECTIONS.report.path + REPORT_SECTIONS.welcome.path,
          redirect: SECTIONS.report.path + REPORT_SECTIONS.home.path
        },
        {
          path: SECTIONS.report.path + REPORT_SECTIONS.resources.path,
          name: REPORT_SECTIONS.resources.routeName,
          component: SectionResources,
          meta: {
            blockedReportStatuses: EVERY_PREVIEW_REPORT_STATUS,
            blockedCoachingStatuses: [],
            onAuthorized: onAuthorizedReport,
            sectionTitle: REPORT_SECTIONS.resources.routeTitle,
            title: REPORT_TITLE
          }
        },
        {
          path: SECTIONS.report.path + REPORT_SECTIONS.resumeBuilder.path,
          name: REPORT_SECTIONS.resumeBuilder.routeName,
          component: SectionResumeBuilder,
          meta: {
            blockedReportStatuses: [ReportStatus.NONE],
            blockedCoachingStatuses: [],
            onAuthorized: (to: any, next: any) => {
              if (!useUserStore().userData?.hasResumeBuilderPackage) {
                next()
              } else onUnauthorized(to, next)
            },
            isFullScreen: true, // When set, only the component will be rendered
            sectionTitle: REPORT_SECTIONS.resumeBuilder.routeTitle,
            title: RESUME_BUILDER_TITLE
          }
        },
        {
          path: SECTIONS.report.path + REPORT_SECTIONS.coachingUpsell.path,
          name: REPORT_SECTIONS.coachingUpsell.routeName,
          component: SectionCoachingUpsell,
          meta: {
            blockedReportStatuses: [ReportStatus.NONE],
            blockedCoachingStatuses: EVERY_PAID_COACHING_STATUS,
            onAuthorized: (to: any, next: any) => {
              if (!useUserStore().isCoachingHasEngaged) {
                next()
              } else onUnauthorized(to, next)
            },
            isFullScreen: true, // When set, only the component will be rendered
            sectionTitle: REPORT_SECTIONS.coachingUpsell.routeTitle,
            title: COACHING_UPSELL_TITLE
          }
        },
        {
          path: SECTIONS.report.path + REPORT_SECTIONS.coachingUpsellB.path,
          name: REPORT_SECTIONS.coachingUpsellB.routeName,
          component: SectionCoachingUpsell,
          meta: {
            blockedReportStatuses: [ReportStatus.NONE],
            blockedCoachingStatuses: EVERY_PAID_COACHING_STATUS,
            onAuthorized: (to: any, next: any) => {
              if (!useUserStore().isCoachingHasEngaged) {
                next()
              } else onUnauthorized(to, next)
            },
            isFullScreen: true, // When set, only the component will be rendered
            sectionTitle: REPORT_SECTIONS.coachingUpsellB.routeTitle,
            title: COACHING_UPSELL_TITLE
          }
        },
        {
          path: SECTIONS.report.path + REPORT_SECTIONS.account.path,
          name: REPORT_SECTIONS.account.routeName,
          component: SectionAccount,
          meta: {
            blockedReportStatuses: [ReportStatus.REFUND],
            blockedCoachingStatuses: [],
            onAuthorized: onAuthorizedReport,
            sectionTitle: REPORT_SECTIONS.account.routeTitle,
            title: REPORT_TITLE
          }
        },
        {
          path: SECTIONS.report.path + REPORT_SECTIONS.careersOverview.path,
          name: REPORT_SECTIONS.careersOverview.routeName,
          component: SectionCareersOverview,
          meta: {
            blockedReportStatuses: EVERY_PREVIEW_REPORT_STATUS,
            blockedCoachingStatuses: [],
            onAuthorized: onAuthorizedReport,
            sectionTitle: REPORT_SECTIONS.careersOverview.routeTitle,
            title: REPORT_TITLE
          }
        },
        {
          path: SECTIONS.coaching.path + COACHING_SECTIONS.home.path,
          name: COACHING_SECTIONS.home.routeName,
          component: SectionCoachingHome,
          meta: {
            blockedReportStatuses: [],
            blockedCoachingStatuses: EVERY_UNPAID_COACHING_STATUS,
            title: COACHING_TITLE,
            isAnchored: true
          }
        },
        {
          path: SECTIONS.report.path + REPORT_SECTIONS.groupCoaching.path,
          name: REPORT_SECTIONS.groupCoaching.routeName,
          component: SectionGroupCoaching,
          meta: {
            blockedReportStatuses: EVERY_NON_GROUP_COACHING_REPORT_STATUS,
            blockedCoachingStatuses: [],
            title: GROUP_COACHING_TITLE
          }
        },
        {
          path: SECTIONS.coaching.path + COACHING_SECTIONS.meetingNotes.path,
          name: COACHING_SECTIONS.meetingNotes.routeName,
          component: SectionCoachingMeetingNotes,
          meta: {
            blockedReportStatuses: [],
            blockedCoachingStatuses: EVERY_UNPAID_COACHING_STATUS,
            title: QUIZ_TITLE
          }
        },
        {
          path: SECTIONS.coaching.path + COACHING_SECTIONS.coachingBundle.path,
          name: COACHING_SECTIONS.coachingBundle.routeName,
          component: CoachingCoachingBundle,
          meta: {
            blockedReportStatuses: [],
            blockedCoachingStatuses: [CoachingStatus.NONE, CoachingStatus.FINISHED, CoachingStatus.CANCELED],
            title: COACHING_TITLE
          }
        },
        {
          path: SECTIONS.report.path + REPORT_SECTIONS.careers.path + "/:jobId",
          name: REPORT_SECTIONS.careers.routeName,
          component: SectionCareers,
          meta: {
            blockedReportStatuses: EVERY_PREVIEW_REPORT_STATUS,
            blockedCoachingStatuses: [],
            onAuthorized: onAuthorizedReport,
            sectionTitle: REPORT_SECTIONS.careers.routeTitle,
            title: REPORT_TITLE,
            isAnchored: true
          }
        },
        {
          path: SECTIONS.report.path + REPORT_SECTIONS.newCareers.path,
          name: REPORT_SECTIONS.newCareers.routeName,
          component: SectionNewCareers,
          meta: {
            blockedReportStatuses: EVERY_UNPAID_REPORT_STATUS,
            blockedCoachingStatuses: [],
            onAuthorized: onAuthorizedReport,
            sectionTitle: null,
            title: REPORT_TITLE
          }
        },
        {
          path: SECTIONS.report.path + REPORT_SECTIONS.selectCoach.path,
          redirect: SECTIONS.coaching.path + COACHING_SECTIONS.selectCoach.path
        },
        {
          path: SECTIONS.report.path + REPORT_SECTIONS.testRetake.path,
          name: REPORT_SECTIONS.testRetake.routeName,
          component: SectionTestRetake,
          meta: {
            blockedReportStatuses: [ReportStatus.FINISHED, ReportStatus.REFUND],
            blockedCoachingStatuses: [],
            onAuthorized: (to: any, next: any) => {
              const userStore = useUserStore()
              const reportStore = useReportStore()
              if (
                userStore.getReportStatus === ReportStatus.NONE || // User has not taken the quiz
                reportStore.isReportOutdated || // User has an outdated report that needs to be retaken
                reportStore.hasRetakeQuizAttempts // User has retake attempts left
              ) {
                next()
              } else onUnauthorized(to, next)
            },
            isFullScreen: true, // When set, only the component will be rendered
            sectionTitle: null,
            title: QUIZ_TITLE
          }
        },
        {
          path: SECTIONS.report.path + REPORT_SECTIONS.testAdjustment.path,
          name: REPORT_SECTIONS.testAdjustment.routeName,
          component: SectionTestAdjustment,
          meta: {
            blockedReportStatuses: EVERY_UNPAID_REPORT_STATUS,
            blockedCoachingStatuses: [],
            onAuthorized: onAuthorizedReport,
            sectionTitle: null,
            title: QUIZ_TITLE
          }
        },
        {
          path: SECTIONS.report.path + REPORT_SECTIONS.checkout.path,
          name: REPORT_SECTIONS.checkout.routeName,
          component: SectionCheckout,
          meta: {
            blockedReportStatuses: [],
            blockedCoachingStatuses: [],
            isFullScreen: true, // When set, only the component will be rendered
            sectionTitle: null,
            title: REPORT_TITLE
          }
        },
        {
          path: SECTIONS.report.path + REPORT_SECTIONS.confirmation.path,
          name: REPORT_SECTIONS.confirmation.routeName,
          component: SectionConfirmation,
          meta: {
            allowAnonymous: true,
            blockedReportStatuses: [],
            blockedCoachingStatuses: [],
            isFullScreen: true, // When set, only the component will be rendered
            sectionTitle: SECTIONS.report.children.confirmation.routeTitle,
            title: THANK_YOU_TITLE
          }
        },
        {
          path: SECTIONS.report.path + REPORT_SECTIONS.outdated.path,
          name: REPORT_SECTIONS.outdated.routeName,
          component: SectionOutdated,
          meta: {
            blockedReportStatuses: EVERY_PREVIEW_REPORT_STATUS,
            blockedCoachingStatuses: [],
            onAuthorized: (to: any, next: any) => {
              if (useReportStore().isReportOutdated) {
                next()
              } else onUnauthorized(to, next)
            },
            isFullScreen: true, // When set, only the component will be rendered
            sectionTitle: null,
            title: REPORT_TITLE
          }
        },
        {
          path: SECTIONS.report.path + REPORT_SECTIONS.feedback.path,
          name: REPORT_SECTIONS.feedback.routeName,
          component: SectionFeedback,
          meta: {
            blockedReportStatuses: EVERY_PREVIEW_REPORT_STATUS,
            blockedCoachingStatuses: [],
            onAuthorized: (to: any, next: any) => {
              if (!useUserStore().isFeedbackExperienceCompleted) {
                next()
              } else onUnauthorized(to, next)
            },
            isFullScreen: true, // When set, only the component will be rendered
            sectionTitle: REPORT_SECTIONS.feedback.routeTitle,
            title: REPORT_TITLE
          }
        }
      ]
    },
    {
      // Catch all route
      path: "/:pathMatch(.*)*",
      redirect: REDIRECT_LOGIN_PAGE
    }
  ]
})

// ***
// Router Guards
// ***

router.beforeEach((to, _from, next) => {
  // Set the document title
  document.title = to.meta?.title ?? DEFAULT_TITLE

  const userStore = useUserStore()

  const blockedReportStatuses = to.meta?.blockedReportStatuses ?? []
  const blockedCoachingStatuses = to.meta?.blockedCoachingStatuses ?? []
  const allowAnonymous = to.meta?.allowAnonymous ?? false

  // User is not logged in and the route allows anonymous access
  // User is logged in and the route is not blocked for their report or coaching status
  if (
    (!userStore.isLoggedIn && allowAnonymous) ||
    (userStore.isLoggedIn &&
      !blockedReportStatuses.includes(userStore.getReportStatus) &&
      !blockedCoachingStatuses.includes(userStore.getCoachingStatus))
  ) {
    onAuthorized(to, next)

    handleZigpoll(to)
  } else {
    onUnauthorized(to, next)
  }
})

router.afterEach((to, from) => {
  // Ensure that the previous route is not the same as the current one
  if (to.path === from.path) return
  GtmService.pageViewEvent()
})

// ***
// Helper functions
// ***
async function onUnauthorized(to: RouteLocationNormalized, next: NavigationGuardNext) {
  const onUnauthenticated = () => {
    localStorage.setItem("redirect", to.path)

    // Convert URLSearchParams to a plain object
    const queryParams = Object.fromEntries(new URLSearchParams(window.location.search).entries())

    router.push({ name: REDIRECT_UNAUTHENTICATED, query: queryParams })
  }

  const userStore = useUserStore()
  if (!userStore.isLoggedIn) onUnauthenticated()
  else if (!userStore.isReportUser && !userStore.isCoachingUser) router.push({ name: REDIRECT_TEST_RETAKE })
  else if (userStore.isReportUser) {
    if (userStore.getReportStatus === ReportStatus.FINISHED) router.push({ name: REDIRECT_REPORT_PAYWALL })
    else router.push({ name: REDIRECT_REPORT_PAGE })
  } else if (userStore.isCoachingUser) {
    if (userStore.getCoachingStatus === CoachingStatus.FINISHED)
      router.push({
        name: userStore.isExecutiveCoachingPotential ? REDIRECT_COACHING_CHOOSE_PLAN : REDIRECT_COACHING_SELECT_PLAN
      })
    else router.push({ name: REDIRECT_COACHING_HOME })
  } else onUnauthenticated()
}

function onAuthorized(to: RouteLocationNormalized, next: NavigationGuardNext) {
  // Routes with ':jobId' param are handled differently
  if ("jobId" in to.params) return handleJobIdParam(to, next)

  // Execute default route processing if the route does not have ':jobId'
  if (!to.meta?.onAuthorized) {
    return next()
  }

  return to.meta?.onAuthorized(to, next)
}

function onAuthorizedReport(to: RouteLocationNormalized, next: NavigationGuardNext) {
  const userStore = useUserStore()

  if (EVERY_FULL_REPORT_STATUS.includes(userStore.getReportStatus)) {
    const reportStore = useReportStore()

    // Redirect to the outdated page if the report is outdated
    if (reportStore.isReportOutdated && !localStorage.getItem("hasSkippedOutdatedRetake")) {
      return router.push({ name: REPORT_SECTIONS.outdated.routeName })
    }

    // Redirect to the feedback page if the user has not completed the feedback
    if (!userStore.isFeedbackExperienceCompleted) {
      return router.push({ name: REPORT_SECTIONS.feedback.routeName })
    }
  }

  return next()
}

/**
 * Handle the 'jobId' parameter in the URL
 */
async function handleJobIdParam(to: RouteLocationNormalized, next: NavigationGuardNext) {
  // Ensure the 'jobId' parameter is a number
  const idToParse = typeof to.params.jobId === "string" ? to.params.jobId : to.params.jobId?.[0]
  const jobIdParam = parseInt(idToParse, 10) // Ensure safe access to params

  const reportStore = useReportStore()

  // Make sure the jobIdParam is valid, otherwise use the default jobId
  const jobId =
    typeof jobIdParam !== "number" ? reportStore.featuredJobId : await useReportStore().setActiveJob(jobIdParam)

  if (!jobId || isNaN(jobId) || jobId === 0) {
    reportStore.notifyIfNoJobs()
    return next({ name: REPORT_SECTIONS.newCareers.routeName })
  }

  // Do not change the route if the correct jobId is already set
  if (jobId === jobIdParam) return next()

  // Redirect to the correct route with the jobId
  if (typeof to.name === "string") {
    return next({
      name: to.name,
      params: { jobId: jobId.toString() }
    })
  }
}

function handleZigpoll(to: RouteLocationNormalized) {
  const reportStore = useReportStore()

  if (sessionStorage.getItem("free-upgrade-report-zigpoll-loaded") || reportStore.reportZigpollLoaded) return

  if (
    String(to.name).startsWith("report") &&
    ![
      GlobalVariables.urls.report.children.feedback.routeName,
      GlobalVariables.urls.report.children.resumeBuilder.routeName,
      GlobalVariables.urls.report.children.coachingUpsell.routeName,
      GlobalVariables.urls.report.children.coachingUpsellB.routeName,
      GlobalVariables.urls.report.children.confirmation.routeName
    ].includes(to.name as string)
  ) {
    reportStore.incrementPageVisits()
  }
  if (reportStore.pageVisits === 8) {
    reportStore.loadZigpoll()
    reportStore.renderZigpoll(reportStore.getReportZigpollId)
    if (reportStore.isFreeUpgradeEligible) sessionStorage.setItem("free-upgrade-report-zigpoll-loaded", "true")
  }
}

export default router
